/**
 * The "client" is a string, which is used to identify the tenant of the Foundation Player.
 *
 * @see https://docs.player.foundation/v1/integration/remote-config
 */
export const Client = {
  /**
   * Client for all G+J products like VIP, Gala, Schöner Wohnen, etc.
   */
  GrunerUndJahr: 'grunerundjahr',

  /**
   * Client for RTL+ MPA Watch at plus.rtl.de
   */
  RTLPlusWatch: 'rtlplusmpawatch',

  /**
   * Client for RTL+ MPA Radio at plus.rtl.de
   */
  RTLPlusRadio: 'rtlplusmparadio',

  /**
   * Client for RTL+ MPA Audiobook at plus.rtl.de
   */
  RTLPlusAudiobook: 'rtlplusmpaaudiobook',

  /**
   * Client for RTL+ MPA Podcast at plus.rtl.de
   *
   * @remarks
   * This client will disable Advertising, because audio ads are embedded in the podcast files and video are should not be displayed.
   */
  RTLPlusPodcast: 'rtlplusmpapodcast',

  /**
   * Client for SuperRTL Toggo at toggo.de
   */
  SuperRTLToggo: 'superrtltoggo',

  /**
   * Client for RTL Media Hub
   *
   * @remarks
   * This client will disable Advertising, because the client is not monetized.
   */
  MediaHub: 'mediahub',

  /**
   * Client for news products of RTL
   */
  RTLDigitalNews: 'rtldigitalnews',

  /**
   * Client for in house products, corporate sites and other non-commercial products
   */
  CorporateSites: 'corporatesites',

  /**
   * Client for the embedded player. The embedded player makes it possible
   * to include RTL content on any site with an iframe. This is similar to
   * youtube iframe embeds.
   */
  Embedded: 'embedded',
} as const;

/**
 * {@inheritDoc Client:var}
 */
export type Client = (typeof Client)[keyof typeof Client];

/**
 * The "scope" is a string, which is used to identify a sub client, where the player is used like a specific app of a client.
 *
 * @see https://docs.player.foundation/v1/integration/remote-config
 */
export const Scope = {
  [Client.RTLPlusWatch]: {
    /**
     * Scope for the MPA website which is used by evergreen browsers
     */
    Web: 'web',

    /**
     * Scope for the Samsung Tizen App
     */
    Tizen: 'tizen',

    /**
     * Scope for the LG WebOS App
     */
    LG: 'lg',

    /**
     * Scope for the Telekom Magenta App
     */
    Magenta: 'magenta',

    /**
     * Scope for the Sky App
     *
     * @deprecated - Use `SkyQ` or `SkyStream` instead, depending on the app variant.
     */
    Sky: 'sky',

    /**
     * Scope for the Sky Q App
     */
    SkyQ: 'skyq',

    /**
     * Scope for the Sky Stream App
     */
    SkyStream: 'skystream',

    /**
     * Scope for the GTV variant of the vodafone app
     */
    VodafoneGTV: 'vodafonegtv',

    /**
     * Scope for the VTV variant of the vodafone app
     */
    VodafoneVTV: 'vodafonevtv',

    /**
     * Scope for the Chromecast App
     */
    Chromecast: 'chromecast',

    /**
     * Scope for the TeleColumbus HTML5 App
     */
    TeleColumbus: 'telecolumbus',

    /**
     * Scope for the Kepler WebView App
     */
    Kepler: 'kepler',
  },
  [Client.RTLPlusRadio]: {
    /**
     * Scope for the MPA website which is used by evergreen browsers
     */
    Web: 'web',

    /**
     * Scope for the Samsung Tizen App
     */
    Tizen: 'tizen',

    /**
     * Scope for the LG WebOS App
     */
    LG: 'lg',

    /**
     * Scope for the Telekom Magenta App
     */
    Magenta: 'magenta',

    /**
     * Scope for the Sky App
     */
    Sky: 'sky',

    /**
     * Scope for the GTV variant of the vodafone app
     */
    VodafoneGTV: 'vodafonegtv',

    /**
     * Scope for the VTV variant of the vodafone app
     */
    VodafoneVTV: 'vodafonevtv',

    /**
     * Scope for the Chromecast App
     */
    Chromecast: 'chromecast',

    /**
     * Scope for the TeleColumbus HTML5 App
     */
    TeleColumbus: 'telecolumbus',

    /**
     * Scope for the Kepler WebView App
     */
    Kepler: 'kepler',
  },
  [Client.RTLPlusAudiobook]: {
    /**
     * Scope for the MPA website which is used by evergreen browsers
     */
    Web: 'web',

    /**
     * Scope for the Samsung Tizen App
     */
    Tizen: 'tizen',

    /**
     * Scope for the LG WebOS App
     */
    LG: 'lg',

    /**
     * Scope for the Telekom Magenta App
     */
    Magenta: 'magenta',

    /**
     * Scope for the Sky App
     */
    Sky: 'sky',

    /**
     * Scope for the GTV variant of the vodafone app
     */
    VodafoneGTV: 'vodafonegtv',

    /**
     * Scope for the VTV variant of the vodafone app
     */
    VodafoneVTV: 'vodafonevtv',

    /**
     * Scope for the Chromecast App
     */
    Chromecast: 'chromecast',

    /**
     * Scope for the TeleColumbus HTML5 App
     */
    TeleColumbus: 'telecolumbus',

    /**
     * Scope for the Kepler WebView App
     */
    Kepler: 'kepler',
  },
  [Client.RTLPlusPodcast]: {
    /**
     * Scope for the MPA website which is used by evergreen browsers
     */
    Web: 'web',

    /**
     * Scope for the Samsung Tizen App
     */
    Tizen: 'tizen',

    /**
     * Scope for the LG WebOS App
     */
    LG: 'lg',

    /**
     * Scope for the Telekom Magenta App
     */
    Magenta: 'magenta',

    /**
     * Scope for the Sky App
     */
    Sky: 'sky',

    /**
     * Scope for the GTV variant of the vodafone app
     */
    VodafoneGTV: 'vodafonegtv',

    /**
     * Scope for the VTV variant of the vodafone app
     */
    VodafoneVTV: 'vodafonevtv',

    /**
     * Scope for the Chromecast App
     */
    Chromecast: 'chromecast',

    /**
     * Scope for the TeleColumbus HTML5 App
     */
    TeleColumbus: 'telecolumbus',

    /**
     * Scope for the Kepler WebView App
     */
    Kepler: 'kepler',
  },
  [Client.RTLDigitalNews]: {
    /**
     * Scope for ntv.de
     */
    NTV: 'ntv',

    /**
     * Scope for the shorts variant of ntv.de
     */
    NTVShorts: 'ntv/shorts',

    /**
     * Scope for Capital+
     */
    Capital: 'capital',

    /**
     * Scope for Geo+
     */
    Geo: 'geo',

    /**
     * Scope for rtl.de
     */
    RTL: 'rtl',

    /**
     * Scope for sport.de
     */
    Sport: 'sport',

    /**
     * Scope for Stern+
     */
    Stern: 'stern',

    /**
     * Scope for the shorts variant of Stern+
     */
    SternShorts: 'stern/shorts',

    /**
     * Scope for wetter.de
     */
    Wetter: 'wetter',

    /**
     * Scope for VOX
     */
    VOX: 'vox',

    /**
     * Scope for the shorts variant of Stern+
     */
    VOXShorts: 'vox/shorts',
  },
  [Client.CorporateSites]: {
    BusinessPortal: 'businessportal',

    Crime: 'crime',

    /**
     * @remarks
     * Not to be confused with the Geo+ scope of RTLDigitalNews
     */
    GeoTV: 'geotv',

    /**
     * Scope for Journalisten Schule, an internal learning platform
     */
    JournalistenSchule: 'journalistenschule',

    /**
     * @remarks
     * Not to be confused with the livingathome scope of Gruner und Jahr
     */
    Living: 'living',

    Nitro: 'nitro',

    NitroShorts: 'nitro/shorts',

    Passion: 'passion',

    /**
     * @remarks
     * Not to be confused with the Super RTL client
     */
    RTLSuper: 'rtlsuper',

    RTLSuperShorts: 'rtlsuper/shorts',

    RTLUp: 'rtlup',

    RTLUpShorts: 'rtlup/shorts',

    SchlagerLiebe: 'schlagerliebe',
  },
  [Client.GrunerUndJahr]: {
    BabyClub: 'babyclub',
    Brigitte: 'brigitte',
    Eltern: 'eltern',
    EssenUndTrinken: 'essenundtrinken',
    Gala: 'gala',

    /**
     * @remarks
     * Not to be confused with the Geo+ scope of RTLDigitalNews or the GeoTV scope of CorporateSites
     */
    Geolino: 'geolino',

    /**
     * @remarks
     * Not to be confused with the Living scope of Corporate Sites
     */
    LivingAtHome: 'livingathome',

    SchoenerWohnen: 'schoenerwohnen',
    Urbia: 'urbia',
    VIP: 'vip',
    Vorname: 'vorname',
  },
  [Client.SuperRTLToggo]: {
    /**
     * Scope for the MPA website which is used by evergreen browsers
     */
    Web: 'web',

    /**
     * Scope for the Samsung Tizen App
     */
    Tizen: 'tizen',

    /**
     * Scope for the AMEE client app
     */
    AMEE: 'amee',
  },
} as const;

type DefinedClientScopes = keyof typeof Scope;

/**
 * List of all matching scopes for a client.
 *
 * @privateRemarks
 * Not every Client will have Scopes, so we have to make sure that the provided Client is in the subset of DefinedClientScopes.
 * This is done via `C extends DefinedClientScopes` which resolves the conditional type to the value we seek or `never`.
 *
 * This enabled stuff like this:
 * ```ts
 * const options: ClientOptions = {client: Client.RTLDigitalNews, scope: Scope[Client.RTLDigitalNews].NTV};
 * ```
 */
export type ClientScope<C extends Client | DefinedClientScopes> = C extends DefinedClientScopes
  ? (typeof Scope)[C][keyof (typeof Scope)[C]]
  : never;

/**
 * {@inheritDoc Scope:var}
 */
export type Scope = typeof Scope;
